'use client';

/**
 * Home dashboard readiness hero — a compact SITREP-mini panel.
 *
 * The full readout lives on /metrics; this surface is the daily
 * touchstone the user opens the app to see. Shows the readiness
 * percentage as the headline number, GO / CAUTION / REST as a
 * classification stamp, 5 micro pillar pips (relative levels at a
 * glance), and the AI's one-line action recommendation.
 *
 * Visual language matches the SITREP page (JetBrains Mono, corner
 * brackets, classification banner) so the user reads it as the same
 * instrument family.
 *
 * Tap anywhere → /metrics for the full SITREP.
 */

import Link from 'next/link';
import { Lock, ArrowRight } from 'lucide-react';
import type { IntelligenceReport } from '@/lib/unifiedIntelligence';

interface Props {
  intelligence: IntelligenceReport | null;
  /** Gated for free users post-preview. Same shape, hidden score. */
  gated?: boolean;
}

/**
 * Bands MUST match the verdict thresholds in
 * `unifiedIntelligence.generateInsights`, which are:
 *
 *   >= 80  "Peak conditions — push hard today."
 *   >= 60  "Good to train — standard intensity."
 *   >= 40  "Reduce intensity — recovery is lagging."
 *   <  40  "Rest day recommended — readiness is critically low."
 *
 * They used to be 75 / 55, which is a different set of bands over the same
 * number. At readiness 70 the card stamped a red-flag "CAUTION" while the
 * order rendered directly beneath it read "Good day for progressive overload
 * — push working sets at RPE 7-8" and the plan chip said HARD. One number,
 * two engines, opposite advice on one screen.
 *
 * Three stamps against four verdicts, so 60+ collapses to GO: the engine
 * treats both "peak" and "good to train" as train-as-planned, and the
 * distinction between them is already carried by the prose.
 */
function readinessStamp(value: number): { label: string; tw: string; bar: string; ringHex: string; glow: string } {
  if (value >= 60) return {
    label: 'GO',
    tw: 'text-emerald-400',
    bar: 'bg-emerald-400',
    ringHex: '#34d399',
    glow: 'rgba(74, 222, 128, 0.45)',
  };
  if (value >= 40) return {
    label: 'CAUTION',
    tw: 'text-amber-400',
    bar: 'bg-amber-400',
    ringHex: '#fbbf24',
    glow: 'rgba(251, 191, 36, 0.45)',
  };
  return {
    label: 'REST',
    tw: 'text-red-400',
    bar: 'bg-red-400',
    ringHex: '#f87171',
    glow: 'rgba(248, 113, 113, 0.4)',
  };
}

export default function HomeReadinessCard({ intelligence, gated = false }: Props) {
  if (!intelligence) return null;
  if (!intelligence.confidence?.sessionsAnalyzed) return null;
  // Provisional readiness is the 70 baseline constant, not a measurement.
  // This card's entire job is to render a readiness verdict, so with nothing
  // to report it renders nothing rather than a green "GO 70" on day one.
  if (intelligence.currentState.readinessIsProvisional) return null;

  // === Gated variant — Pro upsell, preserves panel silhouette ============
  if (gated) {
    return (
      <Link
        href="/pricing"
        aria-label="Unlock readiness score"
        className="relative block overflow-hidden rounded-xl border border-amber-500/40 bg-hud-ink/85 active:scale-[0.997] transition shadow-[0_0_30px_-12px_rgba(245,158,11,0.35)]"
      >
        <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500" />
        <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/40" />
        <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/40" />

        <div className="pl-4 pr-4 py-3 flex items-center gap-3">
          <div className="relative w-14 h-14 flex-shrink-0">
            <div aria-hidden className="absolute inset-0 rounded-full border-2 border-amber-500/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-400" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-amber-400 border border-amber-500/40 px-1.5 py-0.5">
                Readiness · Pro
              </span>
            </div>
            <p className="text-[13px] text-slate-300 leading-snug line-clamp-2">
              Subscribe to unlock daily readiness scoring + personalised Today&apos;s Orders.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-400 flex-shrink-0" aria-hidden />
        </div>
      </Link>
    );
  }

  // === Unlocked variant — full SITREP-mini panel =========================
  const readiness = Math.round(intelligence.currentState.readiness);
  const operatorScore = Math.round(intelligence.currentState.operatorScore);
  const action = intelligence.recommendations.today.action;
  const intensity = intelligence.recommendations.today.intensity;
  const breakdown = intelligence.currentState.operatorScoreBreakdown;
  const stamp = readinessStamp(readiness);

  // Pillar pips — show normalized fill 0..1 per pillar
  const pillars = breakdown ? [
    { key: 'STR', pct: breakdown.strength / 30 },
    { key: 'END', pct: breakdown.endurance / 25 },
    { key: 'BW', pct: breakdown.bodyweight / 20 },
    { key: 'REC', pct: breakdown.recovery / 15 },
    { key: 'CON', pct: breakdown.consistency / 10 },
  ] : [];

  const intensityLabel = intensity ? intensity.toUpperCase() : '';

  return (
    <Link
      href="/metrics"
      aria-label="View full SITREP"
      className="relative block overflow-hidden rounded-xl border border-slate-800/80 bg-hud-ink/90 active:scale-[0.997] transition shadow-[0_0_40px_-16px_rgba(245,158,11,0.25)]"
    >
      {/* Severity bar */}
      <span aria-hidden className={`absolute top-0 bottom-0 left-0 w-1 ${stamp.bar}`} />
      {/* Corner brackets */}
      <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/40" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/40" />

      {/* Classification banner */}
      <div className="pl-4 pr-4 pt-2 pb-1 flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-[0.22em]">
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${stamp.bar} animate-hud-blink`} aria-hidden />
          <span className="text-amber-500/90">Readiness</span>
          <span className="text-slate-700">·</span>
          <span className="text-slate-500 hidden sm:inline">24h Sitrep</span>
        </div>
        <span className={`font-bold border ${stamp.tw} border-current px-1.5 py-0.5`}>
          {stamp.label}
        </span>
      </div>

      {/* Body */}
      <div className="pl-4 pr-4 pb-3 grid grid-cols-[68px_1fr_auto] items-center gap-3 sm:gap-4">
        {/* Number ring */}
        <div className="relative w-[68px] h-[68px]">
          <div
            aria-hidden
            className="absolute inset-0 rounded-full blur-xl"
            style={{ background: `radial-gradient(closest-side, ${stamp.glow}, transparent 70%)`, opacity: 0.7 }}
          />
          <svg width={68} height={68} viewBox="0 0 68 68" className="relative">
            <circle cx={34} cy={34} r={28} fill="none" stroke="rgba(30, 41, 59, 0.65)" strokeWidth={4} />
            <circle
              cx={34} cy={34} r={28} fill="none"
              stroke={stamp.ringHex}
              strokeWidth={4}
              strokeDasharray={`${(readiness / 100) * (2 * Math.PI * 28)} ${2 * Math.PI * 28}`}
              strokeLinecap="round"
              transform="rotate(-90 34 34)"
              style={{ filter: `drop-shadow(0 0 6px ${stamp.glow})` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className={`font-mono text-[24px] font-black tabular-nums leading-none ${stamp.tw}`}
              style={{ textShadow: `0 0 10px ${stamp.glow}` }}
            >
              {readiness}
            </span>
          </div>
        </div>

        {/* Middle — pillars + action */}
        <div className="min-w-0">
          {pillars.length > 0 && (
            <div className="flex items-center gap-1.5 mb-1.5">
              {pillars.map((p) => (
                <div key={p.key} className="flex flex-col items-center gap-0.5">
                  <div className="w-3 h-5 bg-slate-900 border border-slate-800 rounded-[2px] overflow-hidden relative">
                    <div
                      className="absolute bottom-0 left-0 right-0 transition-all duration-700"
                      style={{
                        height: `${Math.max(8, p.pct * 100)}%`,
                        background: p.pct >= 0.7 ? '#34d399' : p.pct >= 0.4 ? '#fbbf24' : '#f87171',
                      }}
                    />
                  </div>
                  <span className="font-mono text-[7px] uppercase tracking-wider text-slate-600">
                    {p.key}
                  </span>
                </div>
              ))}
              <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-500 tabular-nums hidden sm:inline">
                OPS <span className="text-white">{operatorScore}</span>
              </span>
            </div>
          )}
          <p className="text-[13px] text-slate-200 leading-snug line-clamp-2">
            {action || 'Open the SITREP for full context.'}
          </p>
          {intensityLabel && (
            <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
              Today: <span className={stamp.tw}>{intensityLabel}</span>
            </div>
          )}
        </div>

        {/* Right chevron */}
        <ArrowRight className="w-4 h-4 text-amber-500/70" aria-hidden />
      </div>
    </Link>
  );
}
